import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Injector,
  Input,
  OnInit,
  ViewChild
} from '@angular/core'
import {
  ControlContainer,
  ControlValueAccessor,
  FormControl,
  FormControlDirective, FormsModule,
  NG_VALUE_ACCESSOR
} from '@angular/forms'
import {BehaviorSubject, debounceTime, distinctUntilChanged, forkJoin, Observable, of, switchMap} from "rxjs";
import {filter, map, tap} from "rxjs/operators";
import {Backend} from "@generated-backend-types";
import {isArrayLike} from "rxjs/internal/util/isArrayLike";
import {isArray} from "ngx-bootstrap/chronos";
import UuidAttributeData = Backend.App.Data.Meta.UuidAttributeData;
import {TooltipModule} from "ngx-bootstrap/tooltip";
import {NgMultiSelectDropDownModule} from "ng-multiselect-dropdown";
import {ListItem} from '../../../entities/list-item.entity';
import {BaseEndpointService} from '../../../services/api/base-endpoint-service';
import {KeyValuePipe, NgForOf, NgIf} from '@angular/common';
import {TranslatePipe} from '@ngx-translate/core';


@Component({
    selector: 'app-multi-select-with-service-id-array[service][searchOnField]',
    templateUrl: './multi-select-with-service-id-array.component.html',
    changeDetection: ChangeDetectionStrategy.Default,
    providers: [{
            provide: NG_VALUE_ACCESSOR,
            useExisting: MultiSelectWithServiceIdArrayComponent,
            multi: true
        }],
  imports: [TooltipModule, NgMultiSelectDropDownModule, FormsModule, NgIf, TranslatePipe, NgForOf, KeyValuePipe],
  standalone: true
})
export class MultiSelectWithServiceIdArrayComponent<Model extends BaseResource, Data = Model> implements OnInit, ControlValueAccessor {

  @Input() public label!: string
  @Input() public inline = false

  @Input() public small = false
  @Input() public searchOnField?: string;

  @Input()
  public set reloadEmitter(e: EventEmitter<void>) {
    e.subscribe(() => {
      this.reload();
    })
  }

  @Input() public errorMessages!: Record<string, string>
  @Input() public name!: string
  @Input() public prefix?: string
  @Input() public service!: BaseEndpointService<Model, Data>

  @Input() public sort = '-state'

  @Input() public tooltipText?: string
  @Input() public filters?: {
    [param: string]: string | number | boolean | ReadonlyArray<string | number | boolean>;
  }

  @Input() public placeholder?: string

  public selectedItems: ListItem[] = []

  public readonly Infinity = Infinity;

  /**
   * @see https://github.com/nileshpatel17/ng-multiselect-dropdown#settings
   */
  public multiSelectSearchFilterChanged: BehaviorSubject<string | undefined> = new BehaviorSubject<string | undefined>(undefined)
  public multiSelectIsLoading = false;
  public allSelectOptions: (ListItem & { model: Model })[] = [];


  public selectionChanged() {
    const val: string[] = this.selectedItems.map(i => i.id as string)
    this.control?.setValue(val)
  }

  private convertFilters(filters: {
    [param: string]: string | number | boolean | ReadonlyArray<string | number | boolean>;
  }): { key: string, value: string }[] {
    const result: { key: string, value: string }[] = [];
    for (const key in filters) {
      if (filters[key]) {
        const value = filters[key];

        if (Array.isArray(value)) {
          for (const element of value) {
            result.push({key: 'filter[' + key + ']', value: element});
          }
        } else {
          result.push({key: 'filter[' + key + ']', value: value.toString()});
        }
      }
    }
    return result;
  }


  public ngOnInit() {
    this.multiSelectSearchFilterChanged?.next('');
  }


  @ViewChild(FormControlDirective, {static: true})
  public formControlDirective?: FormControlDirective;

  @Input()
  public formControl?: FormControl<Model[] | string | null | undefined | string[]> | FormControl<UuidAttributeData[]>

  @Input()
  public formControlName!: string;

  @Input()
  public closeDropDownOnSelection = false;

  public get control(): FormControl<string[]> {
    if (this.formControl) {
      return <FormControl<string[]>>this.formControl
    }
    const controlCont = this.controlContainer
    if (this.formControlName && controlCont?.control !== null) {
      return controlCont?.control.get(this.formControlName) as FormControl<string[]>;
    }
    return new FormControl([]) as FormControl<string[]>

  }

  get controlContainer(): ControlContainer {
    return this.injector.get(ControlContainer);
  }

  registerOnTouched(fn: () => void): void {
    this.formControlDirective?.valueAccessor?.registerOnTouched(fn);
  }

  registerOnChange(fn: () => void): void {
    this.formControlDirective?.valueAccessor?.registerOnChange(fn);
  }

  writeValue(value: null | undefined | string[] | string): void {
    if (value === '') {
      value = [];
    }
    if (typeof value === "string") {

      value = [value];
    }
    if (value === null || value === undefined) {
      value = [];
    }

    if (!isArrayLike(value)) {
      console.error('write value is not an array', value)
    }

    if (this.control.getRawValue() !== value) {
      this.control?.reset(value, {emitEvent: true});
    }
    this.updateSelectedOptions()


  }

  setDisabledState(): void {
    // da formControl und control identisch sind brauchen wir hier nichts weiter zu geben

  }

  public reload() {

    this.loadData('')

  }

  private updateSelectedOptions() {
    const currentlySelected: string[] = JSON.parse(JSON.stringify(this.control?.value));


    if (!currentlySelected || currentlySelected.length === 0) {
      // aktuell nichts ausgewählt
      this.selectedItems = [];
      this.cdr.detectChanges()
      return
    }


    if (!isArray(currentlySelected)) {

      console.error('currenlty selected is not an array', this.name, currentlySelected);
      return
    }
    const requests = currentlySelected.map((id) => {
      const found: ListItem & { model: Model } | undefined = this.allSelectOptions.find(v => v.model.id === id)
      if (found) {
        return of({data: found.model})
      }
      return this.service?.get(id)
    });

    forkJoin(requests).pipe(
      map((responses) => responses.map((r) => this.service.modelToMultiSelectItem(r.data)))
    ).subscribe({
      next: (data) => {

        this.selectedItems = data;
        this.cdr.markForCheck();
      }, error: (error) => {
        console.error(error)
      }
    });
    return;
  }


  public loadData(query: string) {
    this.loadAllModelsForQuery(query)
      .subscribe(
        {
          next: (allModels) => {

            this.allSelectOptions = allModels;
            this.multiSelectIsLoading = false;
            this.cdr.markForCheck();
            this.updateSelectedOptions()


          },
          error: () => {
            console.error('error when loading data from service');
            this.multiSelectIsLoading = false;
            this.control?.setErrors({
              serverError: 'Invalid Data received from Server!'
            }, {emitEvent: true})
            this.control?.markAsDirty()
            this.control?.markAsTouched()
            this.cdr.markForCheck()
          }
        }
      )
  }


  private loadAllModelsForQuery(query: string): Observable<(ListItem & { model: Model })[]> {
    return of(query).pipe(
      switchMap((query: string) => {

        this.multiSelectIsLoading = true;
        const params: {
          [param: string]: string | number | boolean | ReadonlyArray<string | number | boolean>;
        } = {};
        if (this.searchOnField) {
          params['filter[' + this.searchOnField + ']'] = query;
        }

        params['sort'] = this.sort;
        if (this.filters) {
          const converted = this.convertFilters(this.filters)
          converted.forEach(p => {
            params[p.key] = p.value
          })
        }
        return this.service.getPage(1, params).pipe(map(d => d.data));

      }),
      map((list) => {
        return list.map((model) => this.service.modelToMultiSelectItem(model))
      }),
      tap(() => {

        this.multiSelectIsLoading = false
        this.cdr.detectChanges()
      })
    )
  }

  constructor(private readonly cdr: ChangeDetectorRef,
              private readonly injector: Injector
  ) {
    this.multiSelectSearchFilterChanged.pipe(
      filter(d => d !== undefined),
      distinctUntilChanged(),
      debounceTime(400),
      map(d => d as string)).subscribe(
      d => this.loadData(d)
    )
  }
}
